import { useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { motion } from "framer-motion"
import { isAxiosError } from "axios"
import { loginApi } from "../../features/auth/api/auth.api"
import { tokenStorage } from "../../shared/lib/token"
import {
  EnvelopeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
} from "@heroicons/react/20/solid"
import {
  AcademicCapIcon,
  ChartBarIcon,
  ShieldCheckIcon,
} from "@heroicons/react/16/solid"

const FEATURES = [
  { icon: <AcademicCapIcon className="w-4 h-4" />, text: "Курсы, менторы и GS Academy в одном месте" },
  { icon: <ChartBarIcon className="w-4 h-4" />,     text: "Аналитика по пользователям, курсам и вакансиям" },
  { icon: <ShieldCheckIcon className="w-4 h-4" />,  text: "Модерация контента и обработка жалоб" },
]

export const LoginPage = () => {
  const navigate = useNavigate()

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const res = await loginApi({ email, password })

      const {
        access_token,
        refresh_token,
        role,
        user_id,
      } = res.data

      // ⛔️ Разрешаем только admin и mentor
      if (role !== "admin" && role !== "mentor") {
        throw new Error()
      }

      tokenStorage.set(access_token)
      localStorage.setItem("refresh_token", refresh_token)
      localStorage.setItem("user_id", user_id)
      localStorage.setItem("role", role)

      // 🔀 Роутинг по роли
      if (role === "mentor") {
        navigate("/profile")
      } else {
        navigate("/courses")
      }
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 403 && err.response.data?.detail === "email_not_verified") {
        setError("Почта не подтверждена. Проверьте письмо с кодом подтверждения.")
      } else {
        // ❌ Любая другая ошибка = неправильные данные
        setError("Неправильные email или пароль")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex bg-white">

      {/* ── LEFT: brand panel ── */}
      <div className="hidden lg:flex lg:w-[46%] relative overflow-hidden bg-gradient-to-br from-blue-600 to-indigo-800 flex-col justify-between p-12">
        <div className="absolute top-0 left-1/3 w-96 h-96 rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 -right-20 w-80 h-80 rounded-full bg-indigo-400/20 blur-3xl pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative"
        >
          <img src="/icon-2.png" alt="Vkonekte" className="h-10 w-auto brightness-0 invert" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="relative max-w-md"
        >
          <h2 className="text-3xl font-extrabold text-white leading-tight mb-4">
            Управляй платформой Vkonekte
          </h2>
          <p className="text-blue-100/80 text-sm leading-relaxed mb-8">
            Панель для команды: курсы, пользователи, вакансии, модерация и аналитика —
            всё, что нужно для управления сервисом специалистов маркетплейсов.
          </p>

          <div className="space-y-3.5">
            {FEATURES.map((f) => (
              <div key={f.text} className="flex items-center gap-3 text-sm text-white/90">
                <span className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  {f.icon}
                </span>
                {f.text}
              </div>
            ))}
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="relative text-xs text-blue-200/60"
        >
          © {new Date().getFullYear()} Vkonekte. All rights reserved.
        </motion.p>
      </div>

      {/* ── RIGHT: form ── */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-gray-50 lg:bg-white">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-[380px]"
        >
          {/* Mobile brand mark */}
          <div className="lg:hidden flex justify-center mb-8">
            <img src="/horizontal-1.png" alt="Vkonekte" className="h-14 w-auto object-contain" />
          </div>

          <Link
            to="/"
            className="hidden lg:inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors mb-8"
          >
            <ArrowLeftIcon className="w-3.5 h-3.5" />
            На главную
          </Link>

          <div className="mb-7 text-center lg:text-left">
            <h1 className="text-2xl font-bold text-gray-900">Добро пожаловать</h1>
            <p className="text-sm text-gray-400 mt-1.5">Войдите, чтобы продолжить работу в панели</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-600">
                <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</label>
              <div className="relative">
                <EnvelopeIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  autoComplete="email"
                  className="w-full rounded-xl border border-gray-200 pl-10 pr-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Пароль</label>
              <div className="relative">
                <LockClosedIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="w-full rounded-xl border border-gray-200 pl-10 pr-10 py-2.5 text-sm text-gray-800 placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500 transition-colors"
                >
                  {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="group w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold py-2.5 rounded-xl shadow-sm shadow-blue-200 transition-all hover:shadow-md hover:shadow-blue-300/60 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 disabled:pointer-events-none disabled:hover:translate-y-0"
            >
              {loading ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Войти
                  <ArrowRightIcon className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          </form>

          <div className="flex items-center justify-center gap-1.5 mt-6 text-xs text-gray-400">
            <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-500" />
            Защищённое соединение
          </div>

          <p className="lg:hidden text-center text-xs text-gray-400 mt-8">
            © {new Date().getFullYear()} Vkonekte. All rights reserved.
          </p>
        </motion.div>
      </div>
    </div>
  )
}